import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { Eye, EyeOff, Loader2 } from 'lucide-react';

const LOGO_FULL = "/spt-logo.png";
const LOGO_ICON = "/spt-logo.png";

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('Please enter email and password');
      return;
    }
    
    setLoading(true);
    try {
      await login(email, password);
      toast.success('Welcome back!');
      navigate('/dashboard');
    } catch (err) {
      const message = err.response?.data?.detail || 'Invalid credentials';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left Panel - Graphic */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden bg-[#1F3C4F]">
        {/* Geometric shapes */}
        <div className="absolute inset-0">
          {/* Cyan blob */}
          <div 
            className="absolute -top-20 -left-20 w-96 h-96 rounded-full opacity-80"
            style={{ background: 'radial-gradient(circle, #1F3C4F 0%, transparent 70%)' }}
          />
          {/* Pink blob */}
          <div 
            className="absolute bottom-0 left-1/4 w-80 h-80 rounded-full opacity-70"
            style={{ background: 'radial-gradient(circle, #FF1493 0%, transparent 70%)' }}
          />
          {/* Yellow blob */}
          <div 
            className="absolute top-1/3 right-0 w-72 h-72 rounded-full opacity-70"
            style={{ background: 'radial-gradient(circle, #FFB800 0%, transparent 70%)' }}
          />
          {/* Lime blob */}
          <div 
            className="absolute bottom-1/4 right-1/4 w-64 h-64 rounded-full opacity-60"
            style={{ background: 'radial-gradient(circle, #BFFF00 0%, transparent 70%)' }}
          />
        </div>
        
        {/* Logo centered */}
        <div className="relative z-10 flex flex-col items-center justify-center w-full">
          <div className="bg-white p-6 rounded-lg shadow-2xl">
            <img 
              src={LOGO_ICON} 
              alt="SPT Digital Marketing" 
              className="w-64 h-auto object-contain drop-shadow-2xl animate-fade-in"
            />
          </div>
          <p className="mt-6 text-white/80 text-lg">Digital Marketing Excellence</p>
        </div>
      </div>

      {/* Right Panel - Login Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-slate-50">
        <Card className="w-full max-w-md shadow-xl border-0" data-testid="login-card">
          <CardHeader className="text-center pb-2">
            <div className="bg-white p-4 rounded-lg mx-auto mb-4 lg:hidden">
              <img 
                src={LOGO_FULL} 
                alt="SPT Digital Marketing" 
                className="h-20 w-auto object-contain"
              />
            </div>
            <CardTitle className="text-2xl font-heading text-[#1F3C4F]">Welcome Back</CardTitle>
            <CardDescription className="text-slate-500">
              Sign in to access your workspace
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="h-11"
                  data-testid="login-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="h-11 pr-10"
                    data-testid="login-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>
              <Button 
                type="submit" 
                className="w-full h-11 bg-[#1F3C4F] hover:bg-[#152a38] text-white font-medium"
                disabled={loading}
                data-testid="login-submit-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Signing in...
                  </>
                ) : (
                  'Sign In'
                )}
              </Button>
            </form>
            <div className="mt-6 text-center">
              <Link to="/forgot-password" className="text-sm text-[#1F3C4F] hover:underline">
                Forgot your password?
              </Link>
            </div>
            <div className="mt-6 pt-6 border-t border-slate-200">
              <p className="text-xs text-center text-slate-500">
                SPT Digital Marketing Management System
                <br />
                1026 Softwind Way, Southport NC 28461
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default LoginPage;
